import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, ApiClientError } from '@client/lib/api';
import { formatCents } from '@shared/money';
import styles from './InvoicesPage.module.css';

/** A row in the invoices list, as returned by GET /api/v1/invoices. */
interface InvoiceRow {
  id: string;
  invoiceNumber: string;
  customer: { id: string; name: string } | null;
  issueDate: string;
  totalCents: number;
  amountPaidCents: number;
  balanceDueCents: number;
  status: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'empty' }
  | { kind: 'loaded'; invoices: InvoiceRow[] };

/**
 * Invoices list screen.
 *
 * Money invariant: every figure shown comes straight from the server. The
 * client never sums, never derives the balance, never recomputes tax.
 */
export function InvoicesPage() {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  const load = () => {
    setState({ kind: 'loading' });
    apiGet<InvoiceRow[]>('/api/v1/invoices')
      .then((invoices) => {
        if (invoices.length === 0) {
          setState({ kind: 'empty' });
        } else {
          setState({ kind: 'loaded', invoices });
        }
      })
      .catch((err) => {
        if (err instanceof ApiClientError) {
          setState({ kind: 'error', message: err.message });
        } else {
          setState({ kind: 'error', message: 'Something went wrong.' });
        }
      });
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <main className={styles.container}>
      <h1 className={styles.title}>Invoices</h1>
      {state.kind === 'loading' && (
        <p className={styles.loading} aria-live="polite">
          Loading invoices…
        </p>
      )}
      {state.kind === 'empty' && <p className={styles.empty}>No invoices yet.</p>}
      {state.kind === 'error' && (
        <div className={styles.error} role="alert">
          <p>{state.message}</p>
          <button type="button" className={styles.retryButton} onClick={() => load()}>
            Retry
          </button>
        </div>
      )}
      {state.kind === 'loaded' && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Invoice</th>
              <th>Customer</th>
              <th>Issue date</th>
              <th>Total</th>
              <th>Paid</th>
              <th>Balance</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {state.invoices.map((inv) => (
              <tr key={inv.id}>
                <td>
                  <Link to={`/invoices/${inv.id}`} className={styles.rowLink}>
                    {inv.invoiceNumber}
                  </Link>
                </td>
                <td>{inv.customer?.name ?? '—'}</td>
                <td>{inv.issueDate}</td>
                <td>{formatCents(inv.totalCents)}</td>
                <td>{formatCents(inv.amountPaidCents)}</td>
                <td>{formatCents(inv.balanceDueCents)}</td>
                <td>{inv.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
